import { lockScroll, unlockScroll } from '@theme/utilities';

class AccordionDrawer extends HTMLElement {
  #controller;
  #rows = [];
  #dialog;
  #opener;
  #mobile = window.matchMedia('(max-width: 749px)');

  connectedCallback() {
    if (!this.hasAttribute('data-mobile-drawer')) return;
    this.#dialog = this.querySelector(':scope > dialog');
    if (!this.#dialog) return;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const tabs = this.#dialog.querySelector('[role="tablist"]');
    const content = this.#dialog.querySelector('.product-info-drawer__content');
    this.#rows = Array.from(this.querySelectorAll(':scope > accordion-custom > details')).map((details) => {
      const summary = details.querySelector('summary');
      const panel = details.querySelector('.details-content');
      const tab = document.createElement('button');
      const id = `${this.#dialog.id}-${details.dataset.drawerId}`;
      tab.type = 'button';
      tab.id = `${id}-tab`;
      tab.textContent = details.dataset.drawerHeading;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', `${id}-panel`);
      tabs.append(tab);
      tab.addEventListener('click', () => this.#activate(tab), { signal });
      summary.addEventListener('click', (event) => {
        if (!this.#mobile.matches) return;
        event.preventDefault();
        event.stopPropagation();
        this.#opener = summary;
        this.#rows.forEach((row) => {
          row.panel.id = `${this.#dialog.id}-${row.details.dataset.drawerId}-panel`;
          row.panel.setAttribute('role', 'tabpanel');
          row.panel.setAttribute('aria-labelledby', row.tab.id);
          row.panel.tabIndex = 0;
          content.append(row.panel);
        });
        this.#activate(tab);
        lockScroll(this.#dialog);
        if (event.detail > 0) this.#dialog.dataset.pointerOpen = '';
        else delete this.#dialog.dataset.pointerOpen;
        this.#dialog.showModal();
        tab.focus({ preventScroll: true });
        tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }, { signal, capture: true });
      return { details, summary, panel, tab };
    });
    this.#dialog.addEventListener('close', () => this.#restore(), { signal });
    this.#dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) this.#dialog.close();
      if (event.target === this.#dialog) {
        const rect = this.#dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.#dialog.close();
      }
    }, { signal });
    this.#dialog.addEventListener('keydown', () => {
      delete this.#dialog.dataset.pointerOpen;
    }, { signal });
    tabs.addEventListener('keydown', (event) => {
      const index = this.#rows.findIndex((row) => row.tab === event.target);
      if (index < 0) return;
      const next = { ArrowRight: (index + 1) % this.#rows.length, ArrowLeft: (index - 1 + this.#rows.length) % this.#rows.length, Home: 0, End: this.#rows.length - 1 }[event.key];
      if (next === undefined) return;
      event.preventDefault();
      const tab = this.#rows[next].tab;
      this.#activate(tab);
      tab.focus({ preventScroll: true });
      tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, { signal });
    this.#mobile.addEventListener('change', () => {
      if (!this.#mobile.matches && this.#dialog.open) this.#dialog.close();
      this.#syncTriggers();
    }, { signal });
    this.#syncTriggers();
  }

  #syncTriggers() {
    this.#rows.forEach(({ summary }) => {
      if (this.#mobile.matches) {
        summary.setAttribute('aria-haspopup', 'dialog');
        summary.setAttribute('aria-controls', this.#dialog.id);
      } else {
        summary.removeAttribute('aria-haspopup');
        summary.removeAttribute('aria-controls');
      }
    });
  }

  #activate(active) {
    this.#rows.forEach(({ tab, panel }) => {
      const selected = tab === active;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      panel.hidden = !selected;
    });
    this.#dialog.querySelector('.product-info-drawer__content').scrollTop = 0;
  }

  #restore() {
    this.#rows.forEach(({ details, panel }) => {
      panel.hidden = false;
      panel.removeAttribute('role');
      panel.removeAttribute('aria-labelledby');
      panel.removeAttribute('tabindex');
      panel.removeAttribute('id');
      details.append(panel);
    });
    unlockScroll(this.#dialog);
    this.#opener?.focus({ preventScroll: true });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    if (!this.#dialog) return;
    if (this.#dialog.open) this.#dialog.close();
    this.#restore();
    this.#dialog.querySelector('[role="tablist"]').replaceChildren();
    this.#rows = [];
  }
}

if (!customElements.get('accordion-drawer')) customElements.define('accordion-drawer', AccordionDrawer);
